/**
 * Upper bound for quantity inputs when the Magento max-qty rule does not apply
 * (must stay aligned with cart line-item limits).
 */
export const UNRESTRICTED_MAX_PURCHASE_QTY = 99;

export type MaxQtyStoreConfigSlice = {
  maxqty_enabled?: boolean | string | number | null;
  maxqty_attribute_sets?: string | null;
  maxqty_qty?: number | null;
};

function isMaxQtyEnabledFlag(value: MaxQtyStoreConfigSlice['maxqty_enabled']) {
  if (value === true || value === 1) {
    return true;
  }
  if (value === false || value === 0 || value == null) {
    return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    return normalized === 'true' || normalized === '1';
  }
  return false;
}

export function parseMaxQtyAttributeSetIds(
  raw: MaxQtyStoreConfigSlice['maxqty_attribute_sets']
): Set<string> {
  if (!raw?.trim()) {
    return new Set();
  }
  return new Set(
    raw
      .split(',')
      .map((segment) => segment.trim())
      .filter(Boolean)
  );
}

/**
 * Resolves the max selectable purchase quantity from Magento store config:
 * `maxqty_enabled`, `maxqty_attribute_sets` (comma-separated attribute_set_id),
 * and `maxqty_qty`.
 */
export function getEffectiveMaxPurchaseQuantity(
  config: MaxQtyStoreConfigSlice | null | undefined,
  attributeSetId: number | null | undefined
): number {
  if (!config || !isMaxQtyEnabledFlag(config.maxqty_enabled)) {
    return UNRESTRICTED_MAX_PURCHASE_QTY;
  }

  const allowed = parseMaxQtyAttributeSetIds(config.maxqty_attribute_sets);
  if (!allowed.size || attributeSetId == null) {
    return UNRESTRICTED_MAX_PURCHASE_QTY;
  }

  if (!allowed.has(String(attributeSetId))) {
    return UNRESTRICTED_MAX_PURCHASE_QTY;
  }

  const qty = config.maxqty_qty;
  if (typeof qty !== 'number' || !Number.isFinite(qty) || qty < 1) {
    return UNRESTRICTED_MAX_PURCHASE_QTY;
  }

  return Math.floor(qty);
}
